// lineage.js
// The current lineage of one table cell (Spec 4 section 7): which files state
// the cell now, what each of them stated, and how the column's policy chose
// among their values. It is read from the get_cell_lineage endpoint
// (functions/cloud_functions/tables/get_cell_lineage.py), which answers
//
//   { identifier, column, period_key,
//     policy,     the column's policy; 'Add' for an Add metric
//     belief,     the value the policy chooses for the cell
//     reason,     { text: the policy explanation as a sentence, ... }
//     entries }   one per file that states a value, in the table's row order:
//       { file_id, file_name, file_date, data_profile_id, landed_at,
//         units:  [{ unit_id, unit_name, row_count,
//                    rows: [{ file_id, unit_id, row, period_label, page, record, value }] }],
//         values: [{ value, rows, share }],   the file's most reported values
//         value_count, other_share,           every distinct value, and the share the list leaves out
//         add }                               the file's sum for an Add metric, else null
//
// `column` is a detail or attribute column, with a null `periodKey`, or a
// metric leaf by its dotted path ('Premium.Gross') with the period key of the
// cell ('2026_01'). The lineage is the table as it is when asked: there is no
// "as of" argument.

import { authenticatedFetch } from './api_utils';
import { functionEndpoint } from './endpoints';
import { readApiError } from '../utils/apiError';

// The endpoint's own sentence for a failure it does not explain.
export const LINEAGE_FAILED_MESSAGE = 'The history of this cell could not be read. Please try again.';

// The statuses the endpoint answers with a sentence for the person
// (get_cell_lineage.py): a refusal (400), no valid token (401), no access
// (403), no such table (404) and a workspace being restored (503).
const REFUSAL_STATUSES = [400, 401, 403, 404, 503];

// Resolves to the lineage. A refusal (a table that cannot be read, a column the
// table does not have, a workspace being restored) is thrown with the
// endpoint's sentence and the response's `status`. Every other failure is
// thrown with no `status`, so its text is never shown: a failure to reach the
// endpoint, as it came; any other status, or a page of markup at a refusal
// status (the load balancer's or Google's front end's own error page), as an
// error naming the status.
export async function fetchCellLineage(companyId, tableId, identifier, column, periodKey) {
  const response = await authenticatedFetch(functionEndpoint('get_cell_lineage'), {
    method: 'POST',
    body: JSON.stringify({
      company_id: companyId,
      table_id: tableId,
      identifier,
      column,
      period_key: periodKey,
    }),
  });
  if (!response.ok) {
    const message = await readApiError(response, LINEAGE_FAILED_MESSAGE);
    if (!REFUSAL_STATUSES.includes(response.status) || message.trimStart().startsWith('<')) {
      throw new Error(`get_cell_lineage answered ${response.status}`);
    }
    const refusal = new Error(message);
    refusal.status = response.status;
    throw refusal;
  }
  return response.json();
}
